import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Avatar,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  CheckCircle as CheckIcon,
  PlayArrow as StartIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ro from 'date-fns/locale/ro';
import { api } from '@services/authService';
import { formatDateOnly } from '@utils/dateFormatter';

interface Task {
  id: string;
  title: string;
  body: string;
  status: 'todo' | 'in_progress' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  due_at: string | null;
  created_at: string;
  patient?: any;
}

const DoctorTasks: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    priority: 'medium',
    category: 'other',
    due_at: null as Date | null
  });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/doctor/tasks');
      setTasks(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea task-urilor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTask = async () => {
    try {
      if (editingTask) {
        await api.patch(`/doctor/tasks/${editingTask.id}`, formData);
      } else {
        await api.post('/doctor/tasks', formData);
      }

      setTaskDialogOpen(false);
      resetForm();
      loadTasks();
    } catch (error) {
      console.error('Eroare la salvarea task-ului:', error);
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: string) => {
    try {
      await api.patch(`/doctor/tasks/${taskId}`, { status: newStatus });
      loadTasks();
    } catch (error) {
      console.error('Eroare la actualizarea statusului:', error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Sigur doriti sa stergeti acest task?')) return;

    try {
      await api.delete(`/doctor/tasks/${taskId}`);
      loadTasks();
    } catch (error) {
      console.error('Eroare la stergerea task-ului:', error);
    }
  };

  const openTaskDialog = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        body: task.body,
        priority: task.priority,
        category: task.category,
        due_at: task.due_at ? new Date(task.due_at) : null
      });
    } else {
      resetForm();
    }
    setTaskDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      body: '',
      priority: 'medium',
      category: 'other',
      due_at: null
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: any = {
      reception: 'Receptie',
      medical: 'Medical',
      admin: 'Admin',
      follow_up: 'Follow-up',
      reminder: 'Reminder',
      other: 'Altele'
    };
    return labels[category] || category;
  };

  const renderKanbanColumn = (status: string, title: string, tasks: Task[]) => {
    return (
      <Grid size={{ xs: 12, md: 3 }} key={status}>
        <Card sx={{ minHeight: '500px' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{title}</Typography>
              <Chip label={tasks.length} size="small" />
            </Box>
            <Stack spacing={2}>
              {tasks.map((task) => (
                <Card key={task.id} sx={{ cursor: 'pointer' }} onClick={() => openTaskDialog(task)}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="subtitle2" sx={{ flex: 1 }}>
                        {task.title}
                      </Typography>
                      <Chip
                        label={task.priority}
                        color={getPriorityColor(task.priority)}
                        size="small"
                      />
                    </Box>
                    {task.body && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {task.body.substring(0, 100)}...
                      </Typography>
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Chip
                        label={getCategoryLabel(task.category)}
                        size="small"
                        variant="outlined"
                      />
                      {task.due_at && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDateOnly(task.due_at)}
                        </Typography>
                      )}
                    </Box>
                    {task.patient && (
                      <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                        Pacient: {task.patient.name}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      {status === 'todo' && (
                        <Button
                          size="small"
                          startIcon={<StartIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateTaskStatus(task.id, 'in_progress');
                          }}
                        >
                          Incepe
                        </Button>
                      )}
                      {status === 'in_progress' && (
                        <Button
                          size="small"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateTaskStatus(task.id, 'done');
                          }}
                        >
                          Finalizeaza
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTask(task.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderKanbanView = () => {
    const todoTasks = tasks.filter(t => t.status === 'todo');
    const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
    const doneTasks = tasks.filter(t => t.status === 'done');
    const cancelledTasks = tasks.filter(t => t.status === 'cancelled');

    return (
      <Grid container spacing={2}>
        {renderKanbanColumn('todo', 'De facut', todoTasks)}
        {renderKanbanColumn('in_progress', 'In progres', inProgressTasks)}
        {renderKanbanColumn('done', 'Finalizate', doneTasks)}
        {renderKanbanColumn('cancelled', 'Anulate', cancelledTasks)}
      </Grid>
    );
  };

  const renderListView = () => {
    const activeTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'cancelled');

    return (
      <Stack spacing={2}>
        {activeTasks.map((task) => (
          <Card key={task.id}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h6">{task.title}</Typography>
                    <Chip
                      label={task.priority}
                      color={getPriorityColor(task.priority)}
                      size="small"
                    />
                    <Chip
                      label={task.status === 'todo' ? 'De facut' : 'In progres'}
                      color={task.status === 'todo' ? 'default' : 'warning'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {task.body}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Typography variant="caption">
                      Categorie: {getCategoryLabel(task.category)}
                    </Typography>
                    {task.due_at && (
                      <Typography variant="caption">
                        Termen: {formatDateOnly(task.due_at)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                  {task.status === 'todo' && (
                    <Button
                      size="small"
                      startIcon={<StartIcon />}
                      onClick={() => handleUpdateTaskStatus(task.id, 'in_progress')}
                    >
                      Incepe
                    </Button>
                  )}
                  {task.status === 'in_progress' && (
                    <Button
                      size="small"
                      color="success"
                      startIcon={<CheckIcon />}
                      onClick={() => handleUpdateTaskStatus(task.id, 'done')}
                    >
                      Finalizeaza
                    </Button>
                  )}
                  <IconButton size="small" onClick={() => openTaskDialog(task)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteTask(task.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        ))}
        {activeTasks.length === 0 && (
          <Alert severity="info">Nu exista task-uri active.</Alert>
        )}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Task-uri</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openTaskDialog()}>
            Task nou
          </Button>
        </Box>

        {/* View Toggle */}
        <Box mb={3}>
          <Tabs value={view} onChange={(e, newValue) => setView(newValue)}>
            <Tab label="Kanban Board" value="kanban" />
            <Tab label="Lista" value="list" />
          </Tabs>
        </Box>

        {/* Content */}
        {view === 'kanban' ? renderKanbanView() : renderListView()}

        {/* Task Dialog */}
        <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingTask ? 'Editare Task' : 'Task Nou'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Titlu"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />

              <TextField
                fullWidth
                label="Descriere"
                multiline
                rows={3}
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              />

              <FormControl fullWidth>
                <InputLabel>Prioritate</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Prioritate"
                >
                  <MenuItem value="low">Scazuta</MenuItem>
                  <MenuItem value="medium">Medie</MenuItem>
                  <MenuItem value="high">Ridicata</MenuItem>
                  <MenuItem value="urgent">Urgenta</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Categorie</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Categorie"
                >
                  <MenuItem value="reception">Receptie</MenuItem>
                  <MenuItem value="medical">Medical</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="follow_up">Follow-up</MenuItem>
                  <MenuItem value="reminder">Reminder</MenuItem>
                  <MenuItem value="other">Altele</MenuItem>
                </Select>
              </FormControl>

              <DateTimePicker
                label="Termen limita"
                value={formData.due_at}
                onChange={(newValue) => setFormData({ ...formData, due_at: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTaskDialogOpen(false)}>Anuleaza</Button>
            <Button variant="contained" onClick={handleSaveTask}>
              {editingTask ? 'Actualizeaza' : 'Creeaza'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DoctorTasks;

